import { useEffect, useRef, useState } from "react";

export type PtzMoveLoaderProps = {
  visible: boolean;
  targetMs?: number;
  label?: string;
};

const CIRCUMFERENCE = 2 * Math.PI * 22;
const PROGRESS_CAP_PCT = 95;
const SNAP_HOLD_MS = 140;

export function PtzMoveLoader({
  visible,
  targetMs = 1100,
  label = "PTZ camera moving",
}: PtzMoveLoaderProps) {
  const [mounted, setMounted] = useState(visible);
  const [pct, setPct] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (visible) {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setMounted(true);
      setPct(0);
      startedAtRef.current = performance.now();

      const step = (now: number) => {
        const startedAt = startedAtRef.current ?? now;
        const elapsed = now - startedAt;
        const raw = (elapsed / targetMs) * 100;
        const clamped = raw > PROGRESS_CAP_PCT ? PROGRESS_CAP_PCT : raw;
        setPct(clamped);
        if (clamped < PROGRESS_CAP_PCT) {
          rafRef.current = window.requestAnimationFrame(step);
        }
      };
      rafRef.current = window.requestAnimationFrame(step);

      return () => {
        if (rafRef.current !== null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }

    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (mounted) {
      setPct(100);
      closeTimerRef.current = window.setTimeout(() => {
        setMounted(false);
        setPct(0);
        closeTimerRef.current = null;
      }, SNAP_HOLD_MS);
    }
    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, targetMs]);

  if (!mounted) return null;

  const dashOffset = CIRCUMFERENCE * (1 - pct / 100);

  return (
    <div className="ds-ptz-loader" role="status" aria-live="polite" aria-label={label}>
      <div className="ds-ptz-loader__chip">
        <svg
          className="ds-ptz-loader__svg"
          viewBox="0 0 56 56"
          width="56"
          height="56"
          aria-hidden="true"
        >
          <circle
            className="ds-ptz-loader__track"
            cx="28"
            cy="28"
            r="22"
            fill="none"
            strokeWidth="4"
          />
          <circle
            className="ds-ptz-loader__indicator"
            cx="28"
            cy="28"
            r="22"
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 28 28)"
          />
        </svg>
      </div>
    </div>
  );
}
